import { useEffect, useRef, useState } from 'react';

import { Modal } from '../../components/ui/Modal.js';
import { ApiError, descargarArchivo } from '../../lib/api.js';

export type TipoExamen = 'cleaver' | 'lifo' | 'kostick' | 'terman';

const EXAMENES: ReadonlyArray<{ key: TipoExamen; label: string }> = [
  { key: 'cleaver', label: 'Cleaver — DISC' },
  { key: 'lifo',    label: 'LIFO' },
  { key: 'kostick', label: 'Kostick (PAPI)' },
  { key: 'terman',  label: 'Terman-Merrill' },
];

// Nombre de archivo seguro para Windows/macOS a partir del nombre del candidato.
function nombreArchivo(nombre: string): string {
  const limpio = nombre.replace(/[\\/:*?"<>|]+/g, '').trim() || 'Aspirante';
  return `Resultados psicométricos - ${limpio}.pdf`;
}

// Exporta a PDF los exámenes marcados. Todos los que tienen resultado arrancan
// marcados; los que el candidato no ha terminado aparecen deshabilitados.
// Las notas de Cleaver/LIFO se incluyen solo si existen (lo decide el backend).
export function ExportarResultadosModal({
  open,
  onClose,
  aspiranteId,
  nombreAspirante,
  disponibles,
}: {
  open: boolean;
  onClose: () => void;
  aspiranteId: string;
  nombreAspirante: string;
  disponibles: Record<TipoExamen, boolean>;
}) {
  const [marcados, setMarcados] = useState<Set<TipoExamen>>(new Set());
  const [descargando, setDescargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // La selección se arma solo al abrir el diálogo: si un resultado se
  // refresca con el diálogo abierto, no se re-marca lo que Vero desmarcó.
  const disponiblesRef = useRef(disponibles);
  disponiblesRef.current = disponibles;

  useEffect(() => {
    if (!open) return;
    setMarcados(new Set(EXAMENES.filter((e) => disponiblesRef.current[e.key]).map((e) => e.key)));
    setError(null);
  }, [open]);

  const alternar = (key: TipoExamen) =>
    setMarcados((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const exportar = async () => {
    setDescargando(true);
    setError(null);
    try {
      const seleccion = EXAMENES.filter((e) => marcados.has(e.key)).map((e) => e.key).join(',');
      await descargarArchivo(
        `/psicometricos/aspirantes/${aspiranteId}/exportar-pdf?examenes=${seleccion}`,
        nombreArchivo(nombreAspirante),
      );
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No fue posible generar el PDF');
    } finally {
      setDescargando(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Exportar resultados"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={descargando}>
            Cancelar
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={exportar}
            disabled={descargando || marcados.size === 0}
          >
            {descargando ? 'Generando…' : 'Exportar PDF'}
          </button>
        </>
      }
    >
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
        Desmarca los exámenes que no quieras incluir. Las notas de Cleaver y LIFO se agregan solo si existen.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {EXAMENES.map(({ key, label }) => (
          <label
            key={key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 14,
              color: disponibles[key] ? 'var(--heading)' : 'var(--muted)',
              cursor: disponibles[key] ? 'pointer' : 'not-allowed',
            }}
          >
            <input
              type="checkbox"
              checked={marcados.has(key)}
              disabled={!disponibles[key] || descargando}
              onChange={() => alternar(key)}
            />
            {label}
            {!disponibles[key] && <span style={{ fontSize: 12 }}>(sin resultado)</span>}
          </label>
        ))}
      </div>
      {error && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 12 }}>{error}</div>}
    </Modal>
  );
}
